import React, { useEffect, useState, useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { difference, equals, gt, includes, length, lt, pluck } from "ramda";
import { capitalize } from "util/stringFormat";
import { useHandleOpenViewerClick } from "../../../media-viewer/mediaViewerRedux";
import { notBlank } from "../../../util/ext";
import { useMediaTable } from "../../common/use-mediaTable";
import {
  useResolvedPath,
  useNavigate,
  Link as RouterLink,
} from "react-router-dom";
import { useIsAdmin } from "../../../auth/authUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey } from "@fortawesome/pro-regular-svg-icons";

import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tooltip from "@mui/material/Tooltip";

import ContentCard from "./ContentCard";
import ContentOriginCard from "../../common/ContentOriginCard";
import ContentParentsCard from "../../organize/content-groups/card/ContentParentsCard";
import CustomTablePagination from "../../common/CustomTablePagination";
import PanelLoadingIndicator from "../../../components/PanelLoadingIndicator";
import OwnersList from "../../modules/browse/OwnersList";
import { Typography } from "@mui/material";
import { useStatusStyles, getStatusClass } from "../../common/statusStyles";

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  icon: {
    color: theme.palette.primary.darker,
  },

  contentCell: {
    width: "33%",
    paddingRight: theme.spacing(3),
  },

  cell: {
    minWidth: 80,
  },

  contentTypeCell: {
    textTransform: "capitalize",
  },

  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

const initialCheckbox = {
  checked: false,
  indeterminate: false,
};

const ContentTable = props => {
  const url = useResolvedPath(".").pathname;

  const {
    contentList,
    page,
    onPageChange,
    total = 0,
    onSelectAllContent,
    onSelectChange,
    onRowClick,
    orderBy = "name",
    order = "asc",
    rowsPerPage = 10,
    onRowsPerPageChange,
    onSortChange,
    onScrollVisible,
    searchState,
    selected,
  } = props;

  // The initial search state uses "" for "no sort", which bypasses the
  // destructuring default; TableSortLabel only accepts "asc" | "desc".
  const direction = order || "asc";

  const classes = useStyles();
  const statusClasses = useStatusStyles();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const {
    handleRequestSort,
    handleChangePage,
    handleUpdateRowsPerPage,
  } = useMediaTable(
    {
      onSortChange,
      onPageChange,
      onSelectChange,
      onRowsPerPageChange,
    },
    {
      sort: {
        property: orderBy,
        order,
      },
      page,
      rowsPerPage,
      editId: null,
    },
  );

  const { handleOpenViewerClick, isViewerLoading } = useHandleOpenViewerClick();

  const handleThumbnailClick = content => event => {
    event.stopPropagation();
    handleOpenViewerClick(content);
  };

  const handleTitleClick = content => event => {
    event.stopPropagation();
    navigate(`${url}/${content.id}`);
  };

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    // console.log("Setting scroll visible...");
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible]);

  const [checkbox, setCheckbox] = useState(initialCheckbox);
  useEffect(() => {
    const diff = difference(pluck("id", contentList), selected);

    setCheckbox({
      checked: equals(length(diff), 0),
      indeterminate:
        gt(length(diff), 0) && lt(length(diff), length(contentList)),
    });
  }, [total, selected, contentList]);

  return (
    <Paper elevation={0} className={classes.root}>
      {isViewerLoading && <PanelLoadingIndicator />}
      <TableContainer ref={tableContainerEl}>
        <Table stickyHeader aria-label="Content">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" align="center">
                <Checkbox
                  color="primary"
                  indeterminate={checkbox.indeterminate}
                  checked={checkbox.checked}
                  onChange={event => onSelectAllContent(checkbox, contentList)}
                  inputProps={{ "aria-label": "Select" }}
                />
              </TableCell>

              <TableCell
                key="content"
                align="left"
                classes={{ stickyHeader: classes.headerCell }}
                className={classes.contentCell}
              >
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={direction}
                  onClick={() => handleRequestSort("name")}
                >
                  Title
                  {orderBy === "name" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="status"
                align="left"
                classes={{ stickyHeader: classes.headerCell }}
                className={classes.cell}
              >
                Status
              </TableCell>

              <TableCell
                key="date-uploaded"
                align="left"
                classes={{ stickyHeader: classes.headerCell }}
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "auditAdded"}
                  direction={direction}
                  onClick={() => handleRequestSort("auditAdded")}
                >
                  Uploaded
                  {orderBy === "auditAdded" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="content-type"
                align="left"
                classes={{ stickyHeader: classes.headerCell }}
                className={classes.cell}
              >
                <TableSortLabel
                  active={orderBy === "contentType"}
                  direction={direction}
                  onClick={() => handleRequestSort("contentType")}
                >
                  Content Type
                  {orderBy === "contentType" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell
                key="origin"
                align="left"
                classes={{ stickyHeader: classes.headerCell }}
                className={classes.cell}
              >
                Origin
              </TableCell>

              {isAdmin && (
                <TableCell
                  key="owners"
                  align="left"
                  classes={{ stickyHeader: classes.headerCell }}
                  className={classes.cell}
                >
                  Library/Organization
                </TableCell>
              )}

              <TableCell
                key="included-in"
                align="left"
                classes={{ stickyHeader: classes.headerCell }}
                className={classes.cell}
                style={{ paddingLeft: 20 }}
              >
                Included In
              </TableCell>

              {isAdmin && (
                <TableCell
                  key="manage-content-access"
                  align="center"
                  classes={{ stickyHeader: classes.headerCell }}
                  style={{ width: 50 }}
                >
                  Access Control
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {contentList.map(content => (
              <TableRow
                onClick={() => onRowClick(content)}
                hover
                tabIndex={-1}
                key={content.id}
                role="checkbox"
                aria-checked={includes(content.id, selected)}
                selected={includes(content.id, selected)}
              >
                <TableCell padding="checkbox" align="center">
                  <Checkbox
                    color="primary"
                    checked={includes(content.id, selected)}
                    disabled={content.status === "TAKEDOWN"}
                    inputProps={{
                      "aria-labelledby": "content-checkbox-" + content.id,
                    }}
                  />
                </TableCell>

                <TableCell
                  key={"title:" + content.id}
                  align="left"
                  className={classes.contentCell}
                >
                  <ContentCard
                    content={content}
                    handleThumbnailClick={handleThumbnailClick(content)}
                    handleTitleClick={handleTitleClick(content)}
                  />
                </TableCell>

                <TableCell
                  key={"status:" + content.id}
                  align="left"
                  className={classes.contentCell}
                >
                  <Typography
                    className={getStatusClass(content?.status, statusClasses)}
                  >
                    {capitalize(content?.status ?? "None")}
                  </Typography>
                </TableCell>

                <TableCell key={"date-uploaded:" + content.id} align="left">
                  {new Date(content.auditAdded).toLocaleDateString()}
                </TableCell>

                <TableCell
                  key={"content-type:" + content.id}
                  align="left"
                  className={classes.contentTypeCell}
                >
                  {capitalize(content.type)}
                </TableCell>

                <TableCell key={"origin:" + content.id} align="left">
                  <ContentOriginCard content={content} />
                </TableCell>

                {isAdmin && (
                  <TableCell key={"owners:" + content.id}>
                    <OwnersList media={content} searchState={searchState} />
                  </TableCell>
                )}

                <TableCell key={"included-in: " + content.id}>
                  <ContentParentsCard content={content} />
                </TableCell>

                {isAdmin && (
                  <TableCell
                    key={"content access:" + content.id}
                    align="center"
                  >
                    <Tooltip title="Manage Access">
                      <IconButton
                        component={RouterLink}
                        to={{
                          pathname: `/creator/manage-access/content/${content.id}`,
                        }}
                        state={{
                          label: "Back to All Content",
                          previousLocation: "/creator/content",
                        }}
                        size="large"
                      >
                        <FontAwesomeIcon icon={faKey} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomTablePagination
        component="div"
        count={total}
        rowsPerPage={rowsPerPage}
        page={page}
        onRowsPerPageChange={event =>
          handleUpdateRowsPerPage(+event.target.value)
        }
        onPageChange={(event, page) => {
          handleChangePage(page);
        }}
      />
    </Paper>
  );
};

export default ContentTable;
